import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Shield, KeyRound, Monitor, User as UserIcon, Repeat2, CheckCircle2, LogOut, Bell } from "lucide-react";
import DashboardLayout from "@/layouts/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AccountSettingsPage() {
  const { user, refresh, logout, setActiveMode } = useAuth();
  const [tab, setTab] = useState("profile");

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Account</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">Settings</h1>
          <p className="text-[#252A34]/70 mt-1 text-sm">Manage your profile, security & sessions.</p>
        </div>
        <div className="hidden md:flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-xl">
            <AvatarImage src={user?.picture || undefined} alt={user?.display_name} />
            <AvatarFallback className="rounded-xl bg-[#165a71] text-white">
              {(user?.display_name || "GC").split(" ").map((p) => p[0]).slice(0, 2).join("")}
            </AvatarFallback>
          </Avatar>
          <div className="text-sm">
            <div className="font-medium text-[#071b22]">{user?.display_name}</div>
            <div className="text-xs text-[#252A34]/60">{user?.email}</div>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 rounded-full p-1 mb-8" data-testid="settings-tabs">
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-white" data-testid="tab-profile">
            <UserIcon className="h-4 w-4" /> Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="rounded-full data-[state=active]:bg-white" data-testid="tab-security">
            <Shield className="h-4 w-4" /> Security
          </TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-full data-[state=active]:bg-white" data-testid="tab-sessions">
            <Monitor className="h-4 w-4" /> Sessions
          </TabsTrigger>
          <TabsTrigger value="mode" className="rounded-full data-[state=active]:bg-white" data-testid="tab-mode">
            <Repeat2 className="h-4 w-4" /> Mode
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile"><ProfileTab user={user} onSaved={refresh} /></TabsContent>
        <TabsContent value="security"><SecurityTab user={user} onChanged={refresh} /></TabsContent>
        <TabsContent value="sessions"><SessionsTab onLogoutAll={logout} /></TabsContent>
        <TabsContent value="mode"><ModeTab user={user} onSwitch={setActiveMode} /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function ProfileTab({ user, onSaved }) {
  const [name, setName] = useState(user?.display_name || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user?.display_name || "");
    setPicture(user?.picture || "");
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", { display_name: name, picture });
      await onSaved();
      toast.success("Profile updated.");
    } catch (err) {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 max-w-2xl" data-testid="profile-form">
      <div className="grid gap-6">
        <Field label="Full name">
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className="input" />
        </Field>
        <Field label="Profile picture URL">
          <input value={picture} onChange={(e) => setPicture(e.target.value)} data-testid="profile-picture" className="input" placeholder="https://…" />
        </Field>
        <Field label="Email">
          <input value={user?.email || ""} disabled className="input opacity-60" />
        </Field>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={saving} data-testid="profile-save" className="rounded-full bg-[#165a71] text-white px-5 py-2.5 font-medium hover:bg-[#11485a] transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
          {user?.email_verified ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
              <CheckCircle2 className="h-4 w-4" /> Email verified
            </span>
          ) : (
            <span className="text-xs text-amber-700">Email not verified yet</span>
          )}
        </div>
      </div>
      <style>{INPUT_CSS}</style>
    </form>
  );
}

function SecurityTab({ user, onChanged }) {
  const [setupSecret, setSetupSecret] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupSecret(data);
    } catch (_) {
      toast.error("Could not start 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor auth is now enabled.");
      setCode("");
      setSetupSecret(null);
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password });
      toast.success("Two-factor auth disabled.");
      setPassword("");
      await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to disable");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="security-2fa-card">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-semibold text-[#071b22]">Two-factor authentication</div>
            <div className="text-sm text-[#252A34]/70 mt-1">
              Add an authenticator app (Google Authenticator, 1Password, Authy) as a second step at sign-in.
            </div>
            {user?.two_factor_enabled ? (
              <form onSubmit={disable} className="mt-5 space-y-3">
                <Badge className="bg-emerald-100 text-emerald-800 rounded-full">Enabled</Badge>
                <div className="text-sm text-[#252A34]/70">To disable, confirm your password:</div>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input" data-testid="2fa-disable-password" />
                <button className="rounded-full border border-red-200 text-red-700 px-4 py-2 text-sm font-medium hover:bg-red-50" type="submit" disabled={busy} data-testid="2fa-disable-submit">
                  Disable 2FA
                </button>
              </form>
            ) : setupSecret ? (
              <form onSubmit={enable} className="mt-5 space-y-3">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wider text-[#252A34]/60 font-medium">Secret</div>
                  <div className="mt-1 font-mono text-sm break-all text-[#071b22]" data-testid="2fa-secret">{setupSecret.secret}</div>
                  <div className="text-xs text-[#252A34]/60 mt-3">otpauth URI (paste in your authenticator):</div>
                  <div className="mt-1 font-mono text-[11px] break-all text-[#165a71]">{setupSecret.otpauth_uri}</div>
                </div>
                <label className="block">
                  <span className="text-sm font-medium text-[#071b22]">Enter the 6-digit code from your app</span>
                  <input required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="input mt-1.5" data-testid="2fa-code" />
                </label>
                <button type="submit" disabled={busy} data-testid="2fa-enable-submit" className="rounded-full bg-[#165a71] text-white px-5 py-2.5 font-medium hover:bg-[#11485a] transition-colors disabled:opacity-60">
                  Enable 2FA
                </button>
              </form>
            ) : (
              <button onClick={startSetup} disabled={busy} data-testid="2fa-setup-start" className="mt-4 rounded-full bg-[#071b22] text-white px-5 py-2.5 text-sm font-medium hover:bg-black transition-colors">
                Set up 2FA
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{INPUT_CSS}</style>
    </div>
  );
}

function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success("Session revoked");
      await load();
    } catch (_) {
      toast.error("Could not revoke session");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6 max-w-3xl" data-testid="sessions-list">
      {loading ? (
        <div className="p-4 text-[#252A34]/60 text-sm">Loading sessions…</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {sessions.length === 0 && <li className="p-4 text-sm text-[#252A34]/60">No active sessions.</li>}
          {sessions.map((s) => (
            <li key={s.id} className="p-4 flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center text-[#252A34]/70">
                <Monitor className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#071b22] flex items-center gap-2">
                  {s.kind?.startsWith("google") ? "Google session" : "Email session"}
                  {s.revoked_at ? (
                    <Badge className="rounded-full bg-slate-100 text-[#252A34]/70">Revoked</Badge>
                  ) : (
                    <Badge className="rounded-full bg-emerald-100 text-emerald-800">Active</Badge>
                  )}
                </div>
                <div className="text-xs text-[#252A34]/60 truncate">{s.device_label || "unknown device"}</div>
                <div className="text-xs text-[#252A34]/50">IP {s.ip_address || "—"} · expires {new Date(s.expires_at).toLocaleString()}</div>
              </div>
              {!s.revoked_at && (
                <button onClick={() => revoke(s.id)} data-testid={`revoke-session-${s.id}`} className="text-xs rounded-full border border-slate-200 px-3 py-1.5 text-[#252A34] hover:border-red-300 hover:text-red-700 transition-colors">
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ModeTab({ user, onSwitch }) {
  const modes = user?.available_modes || [];
  const [busy, setBusy] = useState(false);

  const switchTo = async (m) => {
    setBusy(true);
    try {
      await onSwitch(m);
      toast.success(`You're now in ${m} mode.`);
    } catch (_) {
      toast.error("Could not switch mode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl" data-testid="mode-tab">
      {["customer", "provider"].map((m) => {
        const available = modes.includes(m);
        const active = user?.active_mode === m;
        return (
          <motion.div
            key={m}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`rounded-2xl border p-6 bg-white ${active ? "border-[#165a71] ring-2 ring-[#165a71]/20" : "border-slate-200"}`}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
                <Repeat2 className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-lg font-semibold text-[#071b22] capitalize">{m} mode</div>
                <div className="text-xs text-[#252A34]/60">{available ? (active ? "Currently active" : "Available") : "Not enabled yet"}</div>
              </div>
            </div>
            <p className="text-sm text-[#252A34]/75 mt-4">
              {m === "customer" ? "Book pros, hire freelancers, and post custom project requests." : "Get discovered by customers, manage bookings and take payment via the platform."}
            </p>
            <div className="mt-5 flex items-center gap-3">
              {available ? (
                <button
                  onClick={() => switchTo(m)}
                  disabled={busy || active}
                  data-testid={`switch-to-${m}`}
                  className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium hover:bg-[#11485a] transition-colors disabled:opacity-60"
                >
                  {active ? "Currently active" : `Switch to ${m}`}
                </button>
              ) : (
                <a href={m === "provider" ? "/become-a-provider" : "/register"} className="text-sm font-medium text-[#165a71] hover:underline">
                  Enable {m} mode →
                </a>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#071b22]">{label}</span>
    {children}
  </label>
);

const INPUT_CSS = `
.input { width: 100%; border-radius: 0.75rem; border: 1px solid #E2E8F0; background: #fff; padding: 0.75rem 1rem; font-size: 0.95rem; color: #071b22; outline: none; transition: border-color .15s, box-shadow .15s; }
.input:focus { border-color: #165a71; box-shadow: 0 0 0 3px rgba(22,90,113,0.15); }
`;
